/**
 * V2Layout — outer shell for the V2 interface.
 *
 * CSS-grid shell (sidebar + main column). The `.v2-root` wrapper applies the
 * V2 base typography (scoped in v2-tokens.css, imported here as the V2 entry
 * point). Renders <V2Sidebar/> beside a column of <V2TopBar/> + a scroll area
 * hosting the routed page via <Outlet/>.
 *
 * B-7305: below the `md` breakpoint (768 px) the grid is one column and the
 * sidebar becomes a drawer opened from the top bar's menu button. Before this the
 * 244 px rail stayed in a two-column grid and pushed the document 392 px wide at
 * 390 px. The fix lives here, once, instead of in each page.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import '../styles/v2-tokens.css';
import { V2Sidebar } from './V2Sidebar';
import { V2TopBar } from './V2TopBar';
import { VerifyEmailBanner } from '../components/VerifyEmailBanner';

/** Matches Tailwind's `md` breakpoint: below it the shell is a phone shell. */
const PHONE_QUERY = '(max-width: 767px)';

/** True while the viewport is phone width. False where matchMedia is missing (tests). */
function useIsPhone(): boolean {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(PHONE_QUERY).matches
      : false;
  const [phone, setPhone] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = () => setPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return phone;
}

/**
 * Renders the V2 application shell.
 *
 * @returns The shell element wrapping the routed V2 page.
 */
export function V2Layout() {
  // Collapse state lives here so the grid column width can react to it.
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const phone = useIsPhone();
  const { pathname } = useLocation();

  // Picking a destination (or widening the window) closes the drawer.
  useEffect(() => setDrawerOpen(false), [pathname, phone]);

  const rail = collapsed && !phone;

  // Fixed shell, scrolling content: only <main> scrolls. The root is exactly one
  // viewport tall (dvh, so a phone's browser bar does not hide the bottom) and its
  // single row is minmax(0,1fr). With the implicit `auto` row, grid items' automatic
  // min height let the row grow to the page's full height, so the whole document
  // scrolled and carried the sidebar footer and top bar off screen. A page that
  // wants a sticky bar uses `sticky top-0` inside <main>; it sticks under the top bar.
  // print: undoes the viewport pin so a printed page is not cut at one screen.
  return (
    <div
      className="v2-root grid h-screen h-[100dvh] grid-cols-1 grid-rows-[minmax(0,1fr)] overflow-hidden bg-[var(--bg-0)] md:[grid-template-columns:var(--v2-cols)] print:h-auto print:grid-rows-none print:overflow-visible"
      style={
        {
          '--v2-cols': `${rail ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)'} 1fr`,
        } as CSSProperties
      }
    >
      <V2Sidebar
        collapsed={rail}
        setCollapsed={setCollapsed}
        mobileOpen={drawerOpen}
        onCloseMobile={() => setDrawerOpen(false)}
      />
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close menu"
          data-testid="v2-drawer-backdrop"
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div className="flex min-h-0 min-w-0 flex-col bg-[var(--bg-0)]">
        <V2TopBar onMenu={() => setDrawerOpen((o) => !o)} menuOpen={drawerOpen} />
        {/* Semantic <main> landmark (not a bare <div>): screen readers get a
            "main content" skip target, and it mirrors the classic MainLayout
            shell so `main` means the same thing in both. B-217. */}
        <main className="min-h-0 flex-1 overflow-y-auto print:overflow-visible">
          <VerifyEmailBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
